import type { ReportAmountsMode, ReportRecentDto } from '@supply-mover-2-0/shared';
import { create } from 'zustand';
import { ReportPreferencesQueryService } from '../services/ReportPreferencesQueryService';
import { useAuthStore } from './authStore';

/**
 * How long a loaded copy of the user's favorites and recent reports is
 * trusted before the next screen that needs it asks again. Long enough that a
 * page of favorite toggles shares one request; short enough that a favorite
 * marked on another device shows up without a reload.
 */
export const REPORT_PREFERENCES_MAX_AGE_MS = 60_000;

export type ReportPreferencesLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ReportPreferencesStoreState {
	/** Whose preferences are held, as `<tenant>:<user>`. Null = nothing loaded. */
	ownerKey: string | null;
	status: ReportPreferencesLoadStatus;
	/** When the preferences were last loaded (epoch ms); 0 = never. */
	loadedAt: number;
	/** Favorite report keys, in the user's order. */
	favorites: ReadonlyArray<string>;
	/** Recently used reports, newest first. */
	recents: ReadonlyArray<ReportRecentDto>;
	/** Reports with a favorite change in flight. */
	savingKeys: ReadonlySet<string>;
	/** PD-17424 — how report money is shown; auto until loaded or changed. */
	amountsMode: ReportAmountsMode;
	/** PD-17425 — the company's own report names, by report key. */
	labels: Readonly<Record<string, string>>;
}

export interface ReportPreferencesStoreActions {
	/**
	 * Make sure the user's preferences are loaded and recent. Any number of
	 * toggles can call this as they mount: they share one request.
	 */
	ensureLoaded: () => Promise<void>;
	/**
	 * Mark or unmark a favorite. The change shows at once and is put back if
	 * the request fails. Resolves true when it was saved.
	 */
	setFavorite: (reportKey: string, isFavorite: boolean) => Promise<boolean>;
	/** Put the favorites in a new order; put back if the request fails. */
	reorderFavorites: (reportKeys: ReadonlyArray<string>) => Promise<boolean>;
	/**
	 * Record that the user opened a report. The report moves to the top of
	 * the recently used list at once; the server's list replaces it when the
	 * request returns. A failure is only logged: a missed view must never get
	 * in the way of the report itself.
	 */
	recordView: (reportKey: string) => Promise<void>;
	/**
	 * PD-17424 — switch report money between auto and exact. Shows at once and
	 * is put back if the save fails. Resolves true when it was saved.
	 */
	setAmountsMode: (amountsMode: ReportAmountsMode) => Promise<boolean>;
	/**
	 * PD-17425 — name a report for the whole company, or (null) give it back
	 * its original name. Resolves true when it was saved.
	 */
	setLabel: (reportKey: string, label: string | null) => Promise<boolean>;
	reset: () => void;
}

const initialState: ReportPreferencesStoreState = {
	ownerKey: null,
	status: 'idle',
	loadedAt: 0,
	favorites: [],
	recents: [],
	savingKeys: new Set<string>(),
	amountsMode: 'auto',
	labels: {},
};

/**
 * Who the preferences belong to. A different tenant, a different user, or a
 * delegated session (which swaps the user) all change the key, so one
 * person's favorites are never shown to another.
 */
export function currentReportPreferencesOwnerKey(): string | null {
	const { user, instance } = useAuthStore.getState();

	if (user == null) {
		return null;
	}

	return `${instance?.alias ?? ''}:${user.instanceUserId ?? user.id}`;
}

function withKey(
	keys: ReadonlySet<string>,
	key: string,
	isPresent: boolean,
): ReadonlySet<string> {
	const next = new Set(keys);

	if (isPresent) {
		next.add(key);
	} else {
		next.delete(key);
	}

	return next;
}

/** The favorites with one report added at the end, or removed. */
export function withFavorite(
	favorites: ReadonlyArray<string>,
	reportKey: string,
	isFavorite: boolean,
): Array<string> {
	const others = favorites.filter((key) => key !== reportKey);

	return isFavorite ? [...others, reportKey] : others;
}

/** The recently used list with one report moved (or added) to the top. */
export function withRecentView(
	recents: ReadonlyArray<ReportRecentDto>,
	reportKey: string,
	viewedAt: string,
	keep: number,
): Array<ReportRecentDto> {
	return [
		{ reportKey, lastViewedDate: viewedAt },
		...recents.filter((recent) => recent.reportKey !== reportKey),
	].slice(0, keep);
}

/** How many recent reports the store keeps, matching the server. */
const RECENTS_KEPT = 10;

// The request shared by every caller that asked while it was in flight.
let loadInFlight: Promise<void> | null = null;

// Counts favorite changes and recorded views as they start and finish. A
// load that sees this number move while it ran may hold lists from before
// the change (a report page loads the favorites for its toggle while it
// records its own view), so it fetches again instead of trusting them.
let changeCount = 0;

/** How many times one load fetches again because something changed under it. */
const MAX_LOAD_ATTEMPTS = 3;

/**
 * The signed-in user's favorite and recently used reports (PD-16919), held
 * once for every favorite toggle on screen (a category page has one per
 * report card) and for the Reporting home page.
 *
 * Not persisted: the rows live on the server so they follow the user across
 * devices, and a stale copy from an earlier session would only show the
 * wrong state until the first load finished.
 */
export const useReportPreferencesStore = create<
	ReportPreferencesStoreState & ReportPreferencesStoreActions
>()((set, get) => ({
	...initialState,

	ensureLoaded: async () => {
		const ownerKey = currentReportPreferencesOwnerKey();

		if (ownerKey == null) {
			return;
		}

		const state = get();
		const isSameOwner = state.ownerKey === ownerKey;

		// A recent answer is reused, and so is a recent failure: a user who may
		// not keep favorites is refused, and every toggle that mounts would
		// otherwise ask again.
		if (
			isSameOwner &&
			(state.status === 'ready' || state.status === 'error') &&
			Date.now() - state.loadedAt < REPORT_PREFERENCES_MAX_AGE_MS
		) {
			return;
		}

		if (isSameOwner && loadInFlight != null) {
			return loadInFlight;
		}

		// A refresh keeps the lists on screen while the new ones load; a
		// different user's lists must not stay up while the new user's load.
		const isRefresh = isSameOwner && state.status === 'ready';

		if (!isRefresh) {
			set(
				isSameOwner
					? { status: 'loading' }
					: { ...initialState, ownerKey, status: 'loading' },
			);
		}

		const request = (async (): Promise<void> => {
			try {
				let changesBefore = changeCount;
				let loaded = await ReportPreferencesQueryService.getMine();

				for (
					let attempt = 1;
					attempt < MAX_LOAD_ATTEMPTS && changeCount !== changesBefore;
					attempt++
				) {
					changesBefore = changeCount;
					loaded = await ReportPreferencesQueryService.getMine();
				}

				if (get().ownerKey !== ownerKey) {
					return;
				}

				// A favorite still being saved is newer than the list, so what
				// its toggle shows wins over what the server said a moment ago.
				let favorites: Array<string> = [...loaded.favorites];
				const current = get();
				for (const key of current.savingKeys) {
					favorites = withFavorite(
						favorites,
						key,
						current.favorites.includes(key),
					);
				}

				set({
					status: 'ready',
					loadedAt: Date.now(),
					favorites,
					recents: loaded.recents,
					amountsMode: loaded.amountsMode ?? 'auto',
					labels: loaded.labels ?? {},
				});
			} catch (error) {
				console.debug(
					'[reportPreferencesStore] Could not load report preferences',
					error,
				);

				if (get().ownerKey !== ownerKey) {
					return;
				}

				set(
					isRefresh
						? { loadedAt: Date.now() }
						: { status: 'error', loadedAt: Date.now() },
				);
			}
		})();

		loadInFlight = request;

		try {
			await request;
		} finally {
			if (loadInFlight === request) {
				loadInFlight = null;
			}
		}
	},

	setFavorite: async (reportKey, isFavorite) => {
		const ownerKey = currentReportPreferencesOwnerKey();

		if (ownerKey == null || get().savingKeys.has(reportKey)) {
			return false;
		}

		// Whatever is held belongs to someone else: start clean rather than
		// adding this report to another person's list.
		if (get().ownerKey !== ownerKey) {
			set({ ...initialState, ownerKey });
		}

		const previous = get().favorites;

		changeCount++;
		set((state) => ({
			favorites: withFavorite(state.favorites, reportKey, isFavorite),
			savingKeys: withKey(state.savingKeys, reportKey, true),
		}));

		try {
			const favorites = isFavorite
				? await ReportPreferencesQueryService.addFavorite(reportKey)
				: await ReportPreferencesQueryService.removeFavorite(reportKey);

			if (get().ownerKey === ownerKey) {
				set((state) => {
					const savingKeys = withKey(state.savingKeys, reportKey, false);
					// Other saves still in flight keep what their toggles show.
					let merged: Array<string> = [...favorites];
					for (const key of savingKeys) {
						merged = withFavorite(merged, key, state.favorites.includes(key));
					}
					return { favorites: merged, savingKeys };
				});
			}

			return true;
		} catch (error) {
			console.debug('[reportPreferencesStore] Could not save the favorite', error);

			if (get().ownerKey === ownerKey) {
				set((state) => ({
					favorites: withFavorite(
						state.favorites,
						reportKey,
						previous.includes(reportKey),
					),
					savingKeys: withKey(state.savingKeys, reportKey, false),
				}));
			}

			return false;
		} finally {
			changeCount++;
		}
	},

	reorderFavorites: async (reportKeys) => {
		const ownerKey = currentReportPreferencesOwnerKey();

		if (ownerKey == null || get().ownerKey !== ownerKey) {
			return false;
		}

		const previous = get().favorites;

		changeCount++;
		set({ favorites: [...reportKeys] });

		try {
			const favorites =
				await ReportPreferencesQueryService.reorderFavorites(reportKeys);

			if (get().ownerKey === ownerKey) {
				set({ favorites });
			}

			return true;
		} catch (error) {
			console.debug(
				'[reportPreferencesStore] Could not save the favorites order',
				error,
			);

			if (get().ownerKey === ownerKey) {
				set({ favorites: previous });
			}

			return false;
		} finally {
			changeCount++;
		}
	},

	recordView: async (reportKey) => {
		const ownerKey = currentReportPreferencesOwnerKey();

		if (ownerKey == null) {
			return;
		}

		if (get().ownerKey === ownerKey) {
			set((state) => ({
				recents: withRecentView(
					state.recents,
					reportKey,
					new Date().toISOString(),
					RECENTS_KEPT,
				),
			}));
		}

		changeCount++;
		try {
			const recents = await ReportPreferencesQueryService.recordView(reportKey);

			if (get().ownerKey === ownerKey) {
				set({ recents });
			}
		} catch (error) {
			console.debug('[reportPreferencesStore] Could not record the view', error);
		} finally {
			changeCount++;
		}
	},

	setAmountsMode: async (amountsMode) => {
		const previous = get().amountsMode;
		if (previous === amountsMode) {
			return true;
		}
		set({ amountsMode });
		changeCount++;
		try {
			await ReportPreferencesQueryService.setAmountsMode(amountsMode);
			return true;
		} catch (error) {
			console.debug('[reportPreferencesStore] Could not save the Amounts choice', error);
			set({ amountsMode: previous });
			return false;
		} finally {
			changeCount++;
		}
	},

	setLabel: async (reportKey, label) => {
		try {
			const labels = await ReportPreferencesQueryService.setLabel(reportKey, label);
			set({ labels });
			return true;
		} catch (error) {
			console.debug('[reportPreferencesStore] Could not save the report name', error);
			return false;
		}
	},

	reset: () => {
		loadInFlight = null;
		changeCount = 0;
		set({ ...initialState });
	},
}));
